import { useParams, Link } from "react-router-dom";
import { useSingleLoan } from "../../../composables/loan/useSingleLoan";
import LineLoader from "../../../components/loader/line-loader";
import { ChevronLeft } from "lucide-react";
import AuthLayout from "../../../components/layouts/auth";
import { LoanProps } from "../../../types/LoanProps";
import LoanStatusBadge from "../../../components/ui/loan-status-badge";
import Button from "../../../components/form/button";
import DefaultModal from "../../../components/modal/default-modal";
import DataLabel from "../../../components/ui/DataLabel";
import { numberWithCommas, ucFirst } from "../../../lib/helper-support";
import moment from "moment";
import { useApi } from "../../../providers/ApiProvider";
import { useState } from "react";
import TransactionStatusBadge from "../../../components/ui/transaction-status-badge";

const SingleLoan = () => {
  const { reference } = useParams();
  const { data: data = {} as LoanProps, isLoading } = useSingleLoan({
    reference: reference as string,
  });

  const [tab, setTab] = useState<string>("guarantors");
  const tabs = ["guarantors", "transaction history"];

  const api = useApi();

  const approval = async (status: string, flagReason?: string) => {
    const res = await api.POST({
      endpoint: `loans/approval`,
      payload: {
        reference,
        status,
        decline_reason: flagReason,
      },
      refreshKeys: [["loans", reference]],
    });
  };

  return (
    <AuthLayout>
      {isLoading && <LineLoader />}
      {!isLoading && data && (
        <div>
          <h2 className="text-[#8D8D8D] font-[600] text-[14px]">
            Loan Details
          </h2>
          <div className="flex items-center pl-0 mt-4 mb-5 space-x-2">
            <Link to={"/loans"}>
              <ChevronLeft size={20} className="-ml-2" />
            </Link>
            <h1 className="font-[600] text-tertiary text-[18px]">
              Loan: {data.reference}
            </h1>
          </div>
          <div className="flex gap-8 flex-wrap mb-5">
            <div>
              <LoanStatusBadge status={data.status} />
            </div>
            <div className="themeTextMuted flex gap-1 flex-col">
              <label>Date created:</label>
              <span className="text-black font-bold">
                {moment(data.created_at).format("MMM D, YYYY")}
              </span>
            </div>
            <div className="themeTextMuted flex gap-1 flex-col">
              <label>Date submitted:</label>
              <span className="text-black font-bold">
                {data.submission_date
                  ? moment(data.submission_date).format("MMM D, YYYY")
                  : "-"}
              </span>
            </div>
            <div className="themeTextMuted flex gap-1 flex-col">
              <label>Approval Date:</label>
              <span className="text-black font-bold">
                {data.approval_date
                  ? moment(data.approval_date).format("MMM D, YYYY")
                  : "-"}
              </span>
            </div>
            <div className="themeTextMuted flex gap-1 flex-col">
              <label>Date Disbursed:</label>
              <span className="text-black font-bold">
                {data.disbursed_at
                  ? moment(data.disbursed_at).format("MMM D, YYYY")
                  : "-"}
              </span>
            </div>
          </div>
          <div className="flex md:flex-row flex-col-reverse md:gap-20 gap-5">
            <div className="grid md:grid-cols-2 gap-4 md:w-3/5 h-fit">
              <DataLabel
                label="Customer Name"
                value={ucFirst(
                  data.user.first_name + " " + data.user.last_name,
                )}
              />
              <DataLabel label="Ctag" value={data.user.username} />
              <DataLabel label="Email" value={data.user.email} />
              <DataLabel label="Amount" value={numberWithCommas(data.amount)} />
              <DataLabel
                label="Repayment Amount"
                value={numberWithCommas(data.repayment_amount)}
              />
              <DataLabel
                label="Repayment Date"
                value={moment(data.repayment_date).format("MMM D, YYYY")}
              />
              <DataLabel
                label="Interest"
                value={numberWithCommas(data.interest)}
              />
              <DataLabel
                label="Amount Paid"
                value={numberWithCommas(data.amount_paid)}
              />
              {data.disbursed_at && (<DataLabel
                label="Outstanding Amount"
                value={numberWithCommas(
                  data.repayment_amount - data.amount_paid,
                )}
              />)}
            </div>
            <div className="md:w-2/5 h-fit gap-4 grid">
              <Link to={`/analytics?search=${data.user.username}`}>
                <Button text="View User Metrics" design="primary-outline" />
              </Link>
              {data.status == "submitted" && (
                <div className="flex flex-row flex-wrap gap-4">
                  <DefaultModal
                    modalButtonTriggerText={
                      <Button
                        className="w-fit px-5"
                        text="Approve & Disburse Loan"
                      />
                    }
                    onConfirmationAction={() => approval("approved")}
                    description="Are you sure you want to approve and disburse this loan"
                  />
                  <DefaultModal
                    modalButtonTriggerText={
                      <Button
                        className="w-fit px-5"
                        design="primary-danger-outline"
                        text="Decline Loan"
                      />
                    }
                    onConfirmationAction={() =>
                      approval("declined", "Declined by admin")
                    }
                    description="Are you sure you want to decline this loan"
                  />
                </div>
              )}
              {data.decline_reason && (
                <DataLabel label="Decline Reason" value={data.decline_reason} />
              )}
            </div>
          </div>
          <div className="mt-10">
            <div className="flex flex-wrap gap-4">
              {tabs.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setTab(item)}
                  className={`${
                    tab === item
                      ? "border-b-2 border-primary text-primary"
                      : "text-gray-500"
                  } font-[600] text-[14px] pb-2`}
                >
                  {ucFirst(item)}
                </button>
              ))}
            </div>
            {/* table */}
            {tab == "guarantors" && (
              <div className="relative overflow-x-auto">
                <table className="mt-1 text-tertiary font-[600] text-[14px] w-full relative z-10 whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-[#E6EAF0]">
                      <td className="px-6 py-3">Name</td>
                      <td className="px-6 py-3">Relationship</td>
                      <td className="px-6 py-3">Phone</td>
                      <td className="px-6 py-3">Email</td>
                      <td className="px-6 py-3">BVN</td>
                      <td className="px-6 py-3">Residential Address</td>
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {data.guarantors?.map((item: any, index: number) => (
                      <tr
                        key={index}
                        className="border-b border-[#E6EAF0] relative"
                      >
                        <td className="px-6 py-3">{item.name}</td>
                        <td className="px-6 py-3">
                          {item.relationship || "-"}
                        </td>
                        <td className="px-6 py-3">
                          {item.phone_number || "-"}
                        </td>
                        <td className="px-6 py-3">{item.email || "-"}</td>
                        <td className="px-6 py-3">{item.bvn || "-"}</td>
                        <td className="px-6 py-3">
                          {item.residential_address || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* table */}
            {tab == "transaction history" && (
              <div className="relative overflow-x-auto">
                <table className="mt-1 text-tertiary font-[600] text-[14px] w-full relative z-10 whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-[#E6EAF0]">
                      <td className="px-6 py-3">Narration</td>
                      <td className="px-6 py-3">Amount</td>
                      <td className="px-6 py-3">Type</td>
                      <td className="px-6 py-3">Transaction Date</td>
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {data.transactions?.map((item: any, index: number) => (
                      <tr
                        key={index}
                        className="border-b border-[#E6EAF0] relative"
                      >
                        <td className="px-6 py-3">{item.narration}</td>
                        <td className="px-6 py-3">
                          {numberWithCommas(item.amount)}
                        </td>
                        <td className="px-6 py-3">
                          <span className={`capitalize ${item.type == 'credit' ? 'text-green-500' : 'text-red-500'}`}>{item.type}</span>
                        </td>
                        <td className="px-6 py-3">
                          {moment(item.transaction_date).format("MMM D, YYYY")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {!isLoading && !data && (
        <div>
          <h1 className="text-[2em] font-[600] text-primary">Loan Not Found</h1>
          <Link
            to={"/loans"}
            className="flex items-center mt-5 space-x-2 font-[400] text-gray-500 hover:underline"
          >
            <ChevronLeft size={13} />
            <span>back to loans</span>
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default SingleLoan;
